import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import { categories } from "@/mocks/categories";

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://superbuy.sbs';

const shoppingGuideJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The Complete Superbuy Shopping Guide for Beginners in 2026",
    "description": "A step-by-step walkthrough on how to find the best products, use a superbuy spreadsheet effectively, place orders securely, and get your items delivered safely.",
    "author": { "@type": "Organization", "name": "Superbuy Spreadsheet" },
    "publisher": { "@type": "Organization", "name": "Superbuy Spreadsheet", "url": siteUrl },
    "datePublished": "2026-06-01",
    "dateModified": "2026-06-21",
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteUrl}/superbuy-shopping-guide` }
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
      { "@type": "ListItem", "position": 2, "name": "Superbuy Shopping Guide for Beginners", "item": `${siteUrl}/superbuy-shopping-guide` }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "How long does a typical superbuy order take?", "acceptedAnswer": { "@type": "Answer", "text": "From placing the order to receiving it at your doorstep, expect 2-6 weeks depending on the shipping method you choose. Express shipping can deliver in under a week, while economy sea freight may take up to two months." } },
      { "@type": "Question", "name": "Can I return items if I do not like them?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, you can return items to the seller while they are still at the superbuy warehouse if the QC photos show issues. Once items have shipped internationally, returns become more complex and expensive, so always review QC photos carefully." } },
      { "@type": "Question", "name": "How much does shipping cost on average?", "acceptedAnswer": { "@type": "Answer", "text": "Shipping costs vary significantly based on weight, dimensions, destination country, and shipping method. Use the shipping calculator on the platform for accurate estimates." } },
      { "@type": "Question", "name": "Is my payment information secure?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. Superbuy uses industry-standard encryption and secure payment gateways. Your financial details are never shared with individual sellers." } },
      { "@type": "Question", "name": "What happens if my package gets lost?", "acceptedAnswer": { "@type": "Answer", "text": "If you purchased shipping insurance and the package is confirmed lost by the carrier, you can file a claim for compensation. Without insurance, compensation options are limited." } }
    ]
  }
];

export default function ShoppingGuidePage() {
  const navigate = useNavigate();

  return (
    <>
      <SEOHead
        title="The Complete Superbuy Shopping Guide for Beginners 2026"
        description="A step-by-step walkthrough on how to find the best products, use a superbuy spreadsheet effectively, place orders securely, and get your items delivered safely — everything a first-time buyer needs to know."
        keywords="superbuy shopping guide, how to use superbuy, superbuy order tutorial, superbuy beginner guide"
        canonicalPath="/superbuy-shopping-guide"
        ogType="article"
        jsonLd={shoppingGuideJsonLd}
      />
      <Navbar />
      <main className="pt-20">
        <div className="px-6 md:px-12 py-4 bg-background-100">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <button onClick={() => navigate("/")} className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
            <span>/</span>
            <span className="text-foreground-800 font-medium">Superbuy Shopping Guide for Beginners</span>
          </nav>
        </div>

        <article className="px-6 md:px-12 py-10 md:py-14 max-w-4xl mx-auto">
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">Shopping Guide</span>
              <span className="text-xs text-foreground-500">Updated June 2026</span>
              <span className="text-xs text-foreground-500">14 min read</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight">
              The Complete Superbuy Shopping Guide for Beginners in 2026
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
              A step-by-step walkthrough on how to find the best products, use a superbuy spreadsheet effectively, place orders securely, and get your items delivered safely &mdash; everything a first-time buyer needs to know.
            </p>
            <img
              src="https://readdy.ai/api/search-image?query=Bright%20minimalist%20photography%20of%20an%20open%20notebook%20with%20a%20detailed%20hand-drawn%20shopping%20flowchart%20beside%20a%20steaming%20cup%20of%20ceramic%20matcha%20tea%20on%20a%20light%20bamboo%20desk%20mat%20with%20soft%20afternoon%20sunlight%20filtering%20through%20a%20nearby%20window%2C%20warm%20oatmeal%20walls%2C%20editorial%20guide%20aesthetic%2C%20calm%20atmosphere&width=1200&height=600&seq=shopping-guide-hero-v2&orientation=landscape"
              alt="Superbuy shopping guide - step by step tutorial"
              className="w-full rounded-xl mt-8"
            />
          </header>

          <div className="space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Why You Need a Shopping Guide for Your First Superbuy Order
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                Shopping through superbuy can feel overwhelming at first. With thousands of sellers, multiple shipping options, and unfamiliar quality control processes, many newcomers make costly mistakes. A <strong className="text-foreground-900">superbuy spreadsheet</strong> combined with the right shopping guide eliminates guesswork and helps you shop like a pro from day one.
              </p>
              <p className="text-foreground-700 leading-relaxed">
                This guide walks you through every stage of the buying journey &mdash; from finding products and verifying sellers to managing shipping logistics and handling customs. Follow these steps and you will avoid the common pitfalls that trip up most first-time buyers.
              </p>
            </section>

            {/* Quick CTA */}
            <div className="bg-accent-50 border border-accent-200 rounded-xl p-6 text-center">
              <p className="text-sm font-semibold text-accent-800 mb-3">
                Ready to start? Head over to our main site for the latest spreadsheet updates.
              </p>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
                Browse superbuy spreadsheet <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            {/* Step 1 */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Step 1: Understanding the Superbuy Platform
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                Superbuy is an international shopping agent that connects buyers worldwide with products from Chinese marketplaces. Think of it as your personal shopping assistant that handles purchasing, quality inspection, storage, and international shipping on your behalf.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-shopping-cart-2-line text-accent-500"></i> What Superbuy Does
                  </h3>
                  <ul className="space-y-2 text-sm text-foreground-600">
                    <li>&bull; Purchases products on your behalf</li>
                    <li>&bull; Stores items in their warehouse</li>
                    <li>&bull; Provides QC inspection photos</li>
                    <li>&bull; Ships internationally to your address</li>
                  </ul>
                </div>
                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-file-list-3-line text-accent-500"></i> How a Spreadsheet Helps
                  </h3>
                  <ul className="space-y-2 text-sm text-foreground-600">
                    <li>&bull; Pre-vetted product links by category</li>
                    <li>&bull; Seller trust scores and ratings</li>
                    <li>&bull; Price comparison across sellers</li>
                    <li>&bull; Shipping cost estimates included</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Step 2 */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Step 2: Finding Products Using Your Spreadsheet
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                The key to efficient superbuy shopping lies in using a well-organized <strong className="text-foreground-900">superbuy spreadsheet</strong>. Browse through these main categories available in our spreadsheets:
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {categories.map((cat) => (
                  <a key={cat.name} href={cat.url} target="_blank" rel="noopener nofollow" className="px-3 py-1.5 bg-background-100 hover:bg-accent-100 text-sm text-foreground-700 hover:text-accent-800 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                    {cat.name}
                  </a>
                ))}
              </div>
              <p className="text-foreground-700 leading-relaxed">
                Each product entry in the spreadsheet includes direct purchase links, estimated prices, seller ratings, and QC photo availability. Start with categories you are familiar with and gradually explore new areas as you gain confidence.
              </p>
            </section>

            {/* Step 3 */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Step 3: How to Place a Secure Order
              </h2>
              <div className="space-y-4">
                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="font-semibold text-foreground-900 mb-2">1. Copy the Product Link</h3>
                  <p className="text-sm text-foreground-600">From your spreadsheet, copy the product URL and paste it into the superbuy search bar on the platform.</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="font-semibold text-foreground-900 mb-2">2. Select Size and Color</h3>
                  <p className="text-sm text-foreground-600">Choose your preferred options carefully. The spreadsheet often includes size charts and color references to help you decide.</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="font-semibold text-foreground-900 mb-2">3. Add to Cart and Pay</h3>
                  <p className="text-sm text-foreground-600">Use the platform&rsquo;s secure payment system. Never send money directly to individual sellers outside the protected checkout.</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="font-semibold text-foreground-900 mb-2">4. Wait for Warehouse Arrival</h3>
                  <p className="text-sm text-foreground-600">The seller ships to the superbuy warehouse. Once arrived, you will receive a notification and QC photos.</p>
                </div>
              </div>
            </section>

            {/* Step 4 - QC */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Step 4: Quality Control Inspection
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                Quality control is what separates superbuy from direct shopping. Once your item arrives at the warehouse, staff take detailed photos so you can verify the product before shipping. Here is what to check:
              </p>
              <ul className="space-y-3 ml-5 list-disc text-foreground-700">
                <li><strong className="text-foreground-900">Logo and Branding:</strong> Verify logos, tags, and packaging match the seller&rsquo;s description.</li>
                <li><strong className="text-foreground-900">Color and Material:</strong> Compare the actual color and fabric against what was shown in the listing.</li>
                <li><strong className="text-foreground-900">Size and Measurements:</strong> Check that the measurements match the size chart you selected.</li>
                <li><strong className="text-foreground-900">Defects and Damage:</strong> Look for stains, tears, loose threads, or manufacturing flaws.</li>
              </ul>
              <p className="mt-4 text-foreground-700 leading-relaxed">
                If the QC photos reveal problems, you can request a return or exchange before the item ships internationally &mdash; saving you from expensive return shipping later.
              </p>
            </section>

            {/* Step 5 - Shipping */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Step 5: Choosing the Right Shipping Method
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Shipping Line</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Speed</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Cost</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Best For</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Express (DHL/FedEx)", "3-7 days", "$$$$", "High-value or urgent items"],
                      ["Air Mail (EMS)", "7-15 days", "$$$", "Medium-value, moderate speed"],
                      ["E-EMS / ePacket", "15-25 days", "$$", "Budget-friendly, decent tracking"],
                      ["Sea / SAL", "30-60 days", "$", "Large hauls, no rush delivery"],
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-background-100">
                        <td className="py-3 px-4 font-medium text-foreground-800">{row[0]}</td>
                        <td className="py-3 px-4 text-foreground-600">{row[1]}</td>
                        <td className="py-3 px-4 text-foreground-600">{row[2]}</td>
                        <td className="py-3 px-4 text-foreground-600">{row[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Safety Section */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Safety Tips for First-Time Buyers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-accent-50 rounded-lg p-5">
                  <h3 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-shield-check-line text-accent-600"></i> Stick to Verified Sellers
                  </h3>
                  <p className="text-sm text-foreground-600">Always check seller ratings in your spreadsheet. Sellers with 95% positive feedback or higher are generally safe bets.</p>
                </div>
                <div className="bg-accent-50 rounded-lg p-5">
                  <h3 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-bank-card-line text-accent-600"></i> Use Official Payment Channels
                  </h3>
                  <p className="text-sm text-foreground-600">Never pay sellers directly. Always process payments through the superbuy platform for full buyer protection.</p>
                </div>
                <div className="bg-accent-50 rounded-lg p-5">
                  <h3 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-truck-line text-accent-600"></i> Insure Valuable Shipments
                  </h3>
                  <p className="text-sm text-foreground-600">For orders over a certain value, always add shipping insurance. It costs a small percentage but protects against loss or damage.</p>
                </div>
                <div className="bg-accent-50 rounded-lg p-5">
                  <h3 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-global-line text-accent-600"></i> Know Your Customs Rules
                  </h3>
                  <p className="text-sm text-foreground-600">Research your country&rsquo;s import duties and restrictions before ordering. Some items may incur additional fees or face restrictions.</p>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {[
                  { q: "How long does a typical superbuy order take?", a: "From placing the order to receiving it at your doorstep, expect 2-6 weeks depending on the shipping method you choose. Express shipping can deliver in under a week, while economy sea freight may take up to two months." },
                  { q: "Can I return items if I do not like them?", a: "Yes, you can return items to the seller while they are still at the superbuy warehouse if the QC photos show issues. Once items have shipped internationally, returns become more complex and expensive, so always review QC photos carefully." },
                  { q: "How much does shipping cost on average?", a: "Shipping costs vary significantly based on weight, dimensions, destination country, and shipping method. As a rough guide, a typical pair of sneakers might cost a certain amount to ship via air mail. Use the shipping calculator on the platform for accurate estimates." },
                  { q: "Is my payment information secure?", a: "Absolutely. Superbuy uses industry-standard encryption and secure payment gateways. Your financial details are never shared with individual sellers. Always use the official platform payment system for maximum protection." },
                  { q: "What happens if my package gets lost?", a: "If you purchased shipping insurance and the package is confirmed lost by the carrier, you can file a claim for compensation. Without insurance, compensation options are limited, which is why insurance is recommended for valuable orders." },
                ].map((faq, i) => (
                  <details key={i} className="bg-background-100 rounded-lg group">
                    <summary className="px-5 py-4 font-medium text-foreground-900 cursor-pointer list-none flex items-center justify-between">
                      {faq.q}
                      <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                      <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                  </details>
                ))}
              </div>
            </section>

            {/* Internal Links */}
            <section className="bg-background-100 rounded-xl p-6">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">Explore More Resources</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-2 px-4 py-3 bg-background-50 rounded-lg text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer text-left">
                  <i className="ri-home-4-line text-accent-500"></i> Back to Homepage
                </button>
                <button onClick={() => { navigate("/superbuy-spreadsheet-guide"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-2 px-4 py-3 bg-background-50 rounded-lg text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer text-left">
                  <i className="ri-book-2-line text-accent-500"></i> Superbuy Spreadsheet Guide
                </button>
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 px-4 py-3 bg-background-50 rounded-lg text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer">
                  <i className="ri-external-link-line text-accent-500"></i> Visit Main Website
                </a>
              </div>
            </section>

            {/* Final CTA */}
            <div className="text-center py-8 bg-primary-500 rounded-xl">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-background-50 mb-3">
                Start Your Shopping Journey Today
              </h2>
              <p className="text-background-50/80 text-sm mb-5 max-w-md mx-auto">
                Access the complete superbuy spreadsheet with thousands of curated finds and start shopping with confidence.
              </p>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 font-semibold text-sm rounded-full hover:bg-background-100 transition-all duration-200 cursor-pointer whitespace-nowrap">
                superbuy spreadsheet <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}